import React, { useState, useEffect, useRef } from 'react';
import { User } from '../../services/authService';
import { CuriosityService, CuriosityContext, CuriosityContent, CuriosityGeneration } from '../../services/curiosityService';
import PageHeader from '../common/PageHeader';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './CuriosityPage.css';

interface CuriosityPageProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const CuriosityPage: React.FC<CuriosityPageProps> = ({
  user,
  onToggleSidebar
}) => {
  const [context, setContext] = useState<CuriosityContext | null>(null);
  const [loadingContext, setLoadingContext] = useState(true);
  const [generatedContent, setGeneratedContent] = useState<CuriosityContent | null>(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generations, setGenerations] = useState<CuriosityGeneration[]>([]);
  const [loadingGenerations, setLoadingGenerations] = useState(false);
  const [selectedGenerationId, setSelectedGenerationId] = useState<number | null>(null);
  const [showHistoryMenu, setShowHistoryMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      loadContext();
      loadGenerations();
    }
  }, [user]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowHistoryMenu(false);
      }
    };

    if (showHistoryMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHistoryMenu]);

  const loadContext = async () => {
    if (!user) return;

    try {
      setLoadingContext(true);
      setError(null);
      const aggregatedContext = await CuriosityService.aggregateContext(user.id);
      setContext(aggregatedContext);
    } catch (err) {
      console.error('Error loading context:', err);
      setError(err instanceof Error ? err.message : 'Failed to load context');
    } finally {
      setLoadingContext(false);
    }
  };

  const loadGenerations = async () => {
    if (!user) return;

    try {
      setLoadingGenerations(true);
      const gens = await CuriosityService.getGenerations();
      setGenerations(gens);
    } catch (err) {
      console.error('Error loading generations:', err);
    } finally {
      setLoadingGenerations(false);
    }
  };

  const handleGenerate = async () => {
    if (!context || !user) return;

    try {
      setGenerating(true);
      setError(null);
      const content = await CuriosityService.generateCuriosityContent(context);
      
      // Save to database
      const savedGeneration = await CuriosityService.saveGeneration(content.content, context);
      
      // Refresh generations list
      await loadGenerations();
      
      // Set as selected and displayed content
      setSelectedGenerationId(savedGeneration.id);
      setGeneratedContent({
        id: savedGeneration.id,
        content: savedGeneration.content,
        generatedAt: savedGeneration.createdAt
      });
    } catch (err) {
      console.error('Error generating content:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate content');
    } finally {
      setGenerating(false);
    }
  };

  const handleSelectGeneration = async (id: number) => {
    try {
      setSelectedGenerationId(id);
      const generation = await CuriosityService.getGeneration(id);
      setGeneratedContent({
        id: generation.id,
        content: generation.content,
        generatedAt: generation.createdAt
      });
      setShowHistoryMenu(false);
    } catch (err) {
      console.error('Error loading generation:', err);
      setError(err instanceof Error ? err.message : 'Failed to load generation');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const rightActions = (
    <div className="curiosity-header-actions">
      <button
        className="curiosity-generate-btn"
        onClick={handleGenerate}
        disabled={generating || loadingContext || !context}
      >
        {generating ? 'Generating...' : 'Generate'}
      </button>
      {generations.length > 0 && (
        <div className="curiosity-menu-container" ref={menuRef}>
          <button
            className="curiosity-menu-btn"
            onClick={() => setShowHistoryMenu(!showHistoryMenu)}
            title="View history"
          >
            ⋮
          </button>
          {showHistoryMenu && (
            <div className="curiosity-menu-dropdown">
              <div className="curiosity-menu-header">History</div>
              <div className="curiosity-menu-history-list">
                {generations.map((gen) => (
                  <button
                    key={gen.id}
                    className={`curiosity-menu-history-item ${selectedGenerationId === gen.id ? 'selected' : ''}`}
                    onClick={() => handleSelectGeneration(gen.id)}
                  >
                    <div className="curiosity-menu-history-item-date">
                      {formatDate(gen.createdAt)}
                    </div>
                    <div className="curiosity-menu-history-item-preview">
                      {gen.contentPreview || gen.content.substring(0, 80) + (gen.content.length > 80 ? '...' : '')}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="curiosity-page">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<span className="curiosity-header-title">Curiosity</span>}
        rightActions={rightActions}
      />

      <div className="curiosity-content">
        {loadingContext ? (
          <div className="curiosity-loading">
            <p>Loading your learning context...</p>
          </div>
        ) : error ? (
          <div className="curiosity-error">
            <p>Error: {error}</p>
            <button onClick={loadContext} className="curiosity-retry-btn">
              Retry
            </button>
          </div>
        ) : (
          <>
            {/* Context Summary */}
            <div className="curiosity-context-section">
              <div className="curiosity-section-header">
                <span className="curiosity-section-title">/context</span>
              </div>
              <div className="curiosity-context-summary">
                <div className="curiosity-context-item">
                  <span className="curiosity-context-label">Books reading:</span>
                  <span className="curiosity-context-value">{context?.books.length || 0}</span>
                </div>
                <div className="curiosity-context-item">
                  <span className="curiosity-context-label">Chapters written:</span>
                  <span className="curiosity-context-value">{context?.chapters.length || 0}</span>
                </div>
                <div className="curiosity-context-item">
                  <span className="curiosity-context-label">Knowledge lessons:</span>
                  <span className="curiosity-context-value">{context?.lessons.length || 0}</span>
                </div>
              </div>
            </div>

            {/* Generated Content */}
            {generatedContent && (
              <div className="curiosity-generated-section">
                <div className="curiosity-section-header">
                  <span className="curiosity-section-title">/generated</span>
                  <span className="curiosity-generated-date">
                    {formatDate(generatedContent.generatedAt)}
                  </span>
                </div>
                <div className="curiosity-generated-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {generatedContent.content}
                  </ReactMarkdown>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!generatedContent && !generating && generations.length === 0 && (
              <div className="curiosity-empty-state">
                <p>Click "Generate" to create personalized learning suggestions based on your current context.</p>
              </div>
            )}

            {/* Generating State */}
            {generating && (
              <div className="curiosity-generating">
                <p>Generating curiosity-driven content...</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CuriosityPage;

